import { useEffect, useReducer } from 'preact/hooks';
import type { APIHistoryItem } from '../core';
import { type Contact, model } from '../app/model';
import { type Language, Prefs, stringsFor } from '../app/prefs';

export type Tab = 'wallet' | 'activity' | 'scan' | 'settings';

export type Sheet =
  | { type: 'send'; prefill?: string }
  | { type: 'receive' }
  | { type: 'tx'; item: APIHistoryItem }
  | { type: 'contacts'; pick?: (c: Contact) => void }
  | { type: 'contactEdit'; contact: Contact }
  | { type: 'legal' }
  | { type: 'exportCore' }
  | { type: 'fileBackup' }
  | { type: 'install' }
  | { type: 'webWarnings' }
  | { type: 'scanner'; onCode: (code: string) => void };

/** Navigation state: current tab and stack of sheets. */
class UIState extends EventTarget {
  tab: Tab = 'wallet';
  sheets: Sheet[] = [];
  obscured = false;

  private changed() {
    this.dispatchEvent(new Event('change'));
  }

  setTab(t: Tab) {
    this.tab = t;
    this.changed();
  }

  open(s: Sheet) {
    this.sheets = [...this.sheets, s];
    this.changed();
  }

  close() {
    this.sheets = this.sheets.slice(0, -1);
    this.changed();
  }

  closeAll() {
    this.sheets = [];
    this.changed();
  }

  setObscured(v: boolean) {
    if (this.obscured === v) return;
    this.obscured = v;
    this.changed();
  }

  setLanguage(l: Language) {
    Prefs.setLanguage(l);
    document.documentElement.lang = l;
    this.changed();
  }
}

export const ui = new UIState();

/** Re-renders the component whenever the model or the UI state changes. */
export function useApp() {
  const [, force] = useReducer((x: number) => x + 1, 0);
  useEffect(() => {
    const h = () => force(0);
    model.addEventListener('change', h);
    ui.addEventListener('change', h);
    return () => {
      model.removeEventListener('change', h);
      ui.removeEventListener('change', h);
    };
  }, []);
  return { model, ui, s: stringsFor(Prefs.currentLanguage()) };
}
